import { useState } from 'react';
import { Star, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn, getInitials } from '@/lib/utils';
import { useSessionEvaluations, useDeleteEvaluation } from '@/hooks/use-evaluation';
import { BALL_LEVEL_COLOURS } from '@/shared/sessionTemplates';
import type { BallLevel } from '@/shared/sessionTemplates';

interface SessionEvaluationsSummaryProps {
  sessionId: string;
  presentCount: number;
}

const CRITERIA: { key: string; label: string }[] = [
  { key: 'attitude',   label: 'Attitude' },
  { key: 'effort',     label: 'Effort' },
  { key: 'engagement', label: 'Engage' },
  { key: 'physical',   label: 'Physical' },
  { key: 'technical',  label: 'Technical' },
  { key: 'tactical',   label: 'Tactical' },
];

function scoreColour(score: number | null): string {
  if (score === null) return 'bg-muted text-muted-foreground';
  if (score === 4) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400';
  if (score === 3) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400';
  if (score === 2) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
}

function average(eval_: {
  attitude: number; effort: number; engagement: number;
  physical: number | null; technical: number | null; tactical: number | null;
}): string {
  const scores = [
    eval_.attitude, eval_.effort, eval_.engagement,
    eval_.physical, eval_.technical, eval_.tactical,
  ].filter((s): s is number => s !== null);
  if (scores.length === 0) return '—';
  return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
}

export function SessionEvaluationsSummary({
  sessionId,
  presentCount,
}: SessionEvaluationsSummaryProps) {
  const { data: evaluations, isLoading } = useSessionEvaluations(sessionId);
  const deleteEvaluation = useDeleteEvaluation();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  if (isLoading) return null;

  const count = evaluations?.length ?? 0;

  return (
    <>
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Star className="w-4 h-4" />
            Evaluations
          </span>
          <span className={cn(
            'text-sm font-normal',
            count === 0 ? 'text-muted-foreground' : count === presentCount ? 'text-green-600' : 'text-amber-600'
          )}>
            {count}/{presentCount} done
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {count === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No evaluations recorded yet. Tap <strong>Eval</strong> on a player above.
          </p>
        ) : (
          <div className="space-y-3">
            {evaluations!.map((e) => {
              const ballColour = e.playerBallLevel
                ? BALL_LEVEL_COLOURS[e.playerBallLevel as BallLevel]
                : null;
              const avg = average(e);

              return (
                <div key={e.id} className="rounded-lg border bg-muted/30 p-3 space-y-2">
                  {/* Player row */}
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {getInitials(e.playerFirstName, e.playerLastName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm text-foreground">
                        {e.playerFirstName} {e.playerLastName}
                      </span>
                      {ballColour && (
                        <span className={cn('ml-2 inline-flex items-center gap-1 text-xs', ballColour.text)}>
                          <span className={cn('w-2 h-2 rounded-full', ballColour.dot)} />
                          {e.playerBallLevel!.charAt(0).toUpperCase() + e.playerBallLevel!.slice(1)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right">
                        <span className="text-lg font-bold text-primary">{avg}</span>
                        <span className="text-xs text-muted-foreground"> /4</span>
                      </div>
                      {e.submittedByMe && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => setPendingDeleteId(e.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Score chips */}
                  <div className="flex flex-wrap gap-1.5">
                    {CRITERIA.map(({ key, label }) => {
                      const score = e[key as keyof typeof e] as number | null;
                      return (
                        <span
                          key={key}
                          className={cn(
                            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
                            scoreColour(score)
                          )}
                        >
                          {label}
                          {score !== null && <strong>{score}</strong>}
                        </span>
                      );
                    })}
                  </div>

                  {/* Notes */}
                  {e.notes && (
                    <p className="text-xs text-muted-foreground italic border-t pt-1.5 mt-1">
                      "{e.notes}"
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>

    <AlertDialog open={!!pendingDeleteId} onOpenChange={(open) => { if (!open && !deleteEvaluation.isPending) setPendingDeleteId(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete evaluation?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove the evaluation. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={async (e) => {
              e.preventDefault();
              const idToDelete = pendingDeleteId;
              if (!idToDelete) return;
              try {
                await deleteEvaluation.mutateAsync(idToDelete);
                setPendingDeleteId(null);
              } catch (err) {
                console.error('Delete evaluation failed:', err);
                alert(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
              }
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
